 function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }const TOKEN_EXPIRY_KEYS = {
    token: 'expire',
    social_token: 'social_expired',
    skillmeo_token: 'skillmeo_expired',
    mooc_token: 'mooc_expired',
};

let expired = false;
const listeners = new Set();

export const subscribeSessionExpiry = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const getSessionExpired = () => expired;

// An explicit call can also be used by pages or non-Axios API clients.
export const showSessionExpired = () => {
    if (expired) return;
    expired = true;
    listeners.forEach((listener) => listener());
};

export const isSessionCallback = () => {
    const params = new URLSearchParams(window.location.search);
    return window.location.pathname === '/sso'
        || window.location.pathname === '/google_login/auth/google'
        || params.has('authorize_code')
        || params.has('mobile_auth')
        || params.get('logout') === 'true';
};

const parseExpiry = (value) => {
    if (!value) return null;
    const numeric = Number(value);
    const time = Number.isFinite(numeric)
        ? numeric * (numeric < 1e12 ? 1000 : 1)
        : Date.parse(value);
    return Number.isFinite(time) ? time : null;
};

export const getTokenExpiresAt = (tokenKey = 'token') => {
    const token = localStorage.getItem(tokenKey);
    if (!token) return null;
    let jwtExpiry = null;
    try {
        const payload = token.split('.')[1];
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')));
        if (Number.isFinite(decoded.exp)) jwtExpiry = decoded.exp * 1000;
    } catch (_error) {
        // Opaque tokens use the expiry supplied by the login response.
    }
    const storedExpiry = parseExpiry(localStorage.getItem(TOKEN_EXPIRY_KEYS[tokenKey]));
    const times = [jwtExpiry, storedExpiry].filter((time) => time !== null);
    return times.length ? Math.min(...times) : null;
};

export const isSessionExpired = (tokenKeys = Object.keys(TOKEN_EXPIRY_KEYS)) =>
    tokenKeys.some((key) => {
        const time = getTokenExpiresAt(key);
        return time !== null && time <= Date.now();
    });

export const checkSessionExpiry = (tokenKeys) => {
    if (!isSessionCallback() && isSessionExpired(tokenKeys)) showSessionExpired();
    return getSessionExpired();
};

export const watchSessionExpiry = (tokenKeys) => {
    let timer;
    const check = () => {
        window.clearTimeout(timer);
        checkSessionExpiry(tokenKeys);
        if (expired) return;
        const remaining = tokenKeys.map(getTokenExpiresAt)
            .filter((time) => time !== null && time > Date.now())
            .map((time) => time - Date.now());
        // Also pick up tokens written in this tab after login without a reload.
        timer = window.setTimeout(check, Math.max(1, Math.min(1000, ...remaining)));
    };
    check();
    window.addEventListener('storage', check);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
        window.clearTimeout(timer);
        window.removeEventListener('storage', check);
        window.removeEventListener('focus', check);
        document.removeEventListener('visibilitychange', check);
    };
};

export const clearExpiredSession = () => {
    Object.entries(TOKEN_EXPIRY_KEYS).forEach(([tokenKey, expiryKey]) => {
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(expiryKey);
    });
    ['social', 'skillmeo', 'mooc'].forEach((app) => {
        localStorage.removeItem(`${app}_refresh_token`);
        localStorage.removeItem(`${app}_refresh_token_expired_at`);
    });
    ['user_info', 'social_user_info', 'mooc_user_info', 'portfolio_user_info'].forEach((key) => localStorage.removeItem(key));
    ['onboarding_data', 'onboarding_role_id', 'ms2_mobile_auth_state'].forEach((key) => sessionStorage.removeItem(key));
    expired = false;
    listeners.forEach((listener) => listener());
};

export const getSessionLoginUrl = () => {
    // logout=true clears the SSO origin's session as well as the current app's.
    const url = new URL(process.env.REACT_APP_MAIN_URL || '/login', window.location.origin);
    url.pathname = `${url.pathname.replace(/\/$/, '').replace(/\/login$/, '')}/login`;
    url.search = '';
    url.hash = '';
    url.searchParams.set('logout', 'true');
    return url.toString();
};

// Login failures must remain ordinary form errors even if a stale token exists.
const isPublicAuthRequest = (config) => {
    if (config.skipSessionExpiry) return true;
    const path = new URL(config.url || '/', window.location.origin).pathname;
    return /\/(?:auth\/(?:login(?:_password|\/sso)?|sso|logout|request_reset_pass|reset_pass(?:\/[^/]+)?)|bma-sso\/login|user\/(?:login|request_reset_password|reset_password|sso_reset_password|reset_token\/[^/]+))\/?$/.test(path);
};

export const attachSessionExpiryInterceptors = (instance, tokenKey = 'token') => {
    const requestId = instance.interceptors.request.use((config) => {
        const token = localStorage.getItem(tokenKey);
        config.headers = config.headers || {};
        if (token && !config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
        if (isPublicAuthRequest(config)) return config;
        if (token && !isSessionCallback() && checkSessionExpiry([tokenKey])) {
            const error = new Error('Expired session');
            error.code = 'SESSION_EXPIRED';
            return Promise.reject(error);
        }
        return config;
    });
    const responseId = instance.interceptors.response.use((response) => response, (error) => {
        const config = error.config || _optionalChain([error, 'access', _ => _.response, 'optionalAccess', _2 => _2.config]);
        const authorization = _optionalChain([config, 'optionalAccess', _3 => _3.headers, 'optionalAccess', _4 => _4.Authorization]) || _optionalChain([config, 'optionalAccess', _5 => _5.headers, 'optionalAccess', _6 => _6.get, 'optionalCall', _7 => _7('Authorization')]);
        const token = localStorage.getItem(tokenKey);
        if (_optionalChain([error, 'access', _8 => _8.response, 'optionalAccess', _9 => _9.status]) === 401 && config && !isPublicAuthRequest(config)
            && !isSessionCallback() && token && authorization === `Bearer ${token}`) {
            showSessionExpired();
        }
        return Promise.reject(error);
    });
    return () => {
        instance.interceptors.request.eject(requestId);
        instance.interceptors.response.eject(responseId);
    };
};
